<i18n>
{
  "en": {
    "confirmTitle": "Are you sure you want to create the Product Indexing?",
    "confirmText": "Changes will be irreversible",
    "mainProduct": "Main product",
    "subordinateProduct": "Subordinate product",
    "ratio": "Ratio",
    "emptyFieldError": "Mandatory field",
    "createTitle": "Create a new product indexing",
    "editTitle": "Edit product indexing",
    "invalidNumberError": "Must be a valid number"
  },
  "es": {
    "confirmTitle": "¿Está seguro de que quiere crear el Product Indexing?",
    "confirmText": "Los cambios serán irreversibles",
    "mainProduct": "Producto principal",
    "subordinateProduct": "Producto subordinado",
    "ratio": "Ratio",
    "emptyFieldError": "Campo obligatorio",
    "createTitle": "Crear una nueva indexación de productos",
    "editTitle": "Modificar una indexación de productos",
    "invalidNumberError": "Tiene que ser un número válido"
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <dynameat-confirmation-modal :visible="confirmModalVisible" :title="$t('confirmTitle')"
                                 :description="$t('confirmText')" :confirm-text="$t('confirm')"
                                 @confirm="onConfirmConfirmModal" @cancel="onCloseConfirmModal"/>
    <master-product-modal :visible="masterProductModalVisible" :product-prop="productToEdit"
                          @close="onCloseMasterProductModal"/>
    <v-card v-if="visible" height="100%" class="px-7 py-5">
      <div class="d-flex align-center justify-space-between">
        <v-card-title class="word-break pa-0">{{ title }}</v-card-title>
      </div>
      <v-form v-model="valid" class="d-flex flex-column align-left" ref="form">
        <dynameat-autocomplete v-model="selectedMainProduct" :items="referenceProductList" :rules="objectRules"
                        :filter="onProductTextFilter" :label="$t('mainProduct')" :disabled="editMode"
                        prepend-icon="mdi-database-search" item-text="name" chips persistent-hint
                        item-value="guid" :required="true">
          <template v-slot:item="data">
            <template>
              <v-list-item-avatar>
                <v-img :src="data.item.image" :alt="data.item.name" :lazy-src="defaultProductImg"
                       contain/>
              </v-list-item-avatar>
              <v-list-item-content class="h-scroll">
                <v-list-item-title class="no-wrap">
                  {{ data.item.commercial_name }} ({{ data.item.first_price }}€)
                </v-list-item-title>
                <v-list-item-subtitle class="no-wrap">{{ data.item.name }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </template>
          <template v-slot:selection="data">
            <v-chip class="caption | d-flex justify-center align-center"
                    :disabled="editMode" :close="!editMode" @click:close="onRemoveMainProduct">
              <div class="chip-ellipsis">{{ data.item.name }}</div>
            </v-chip>
          </template>
        </dynameat-autocomplete>
        <dynameat-autocomplete v-model="selectedSubProduct" :items="subordinateProductList"
                        @change="onChangeSubProduct" :rules="objectRules" :filter="productTextFilter"
                        :disabled="editMode" :label="$t('subordinateProduct')" prepend-icon="mdi-database-search"
                        item-text="name" chips persistent-hint item-value="guid" :required="true">
          <template v-slot:item="data">
            <template>
              <v-list-item-avatar>
                <v-img :src="data.item.image" :alt="data.item.name" :lazy-src="defaultProductImg"
                       contain/>
              </v-list-item-avatar>
              <v-list-item-content class="h-scroll">
                <v-list-item-title class="no-wrap">
                  {{ data.item.commercial_name }} ({{ data.item.first_price }}€)
                </v-list-item-title>
                <v-list-item-subtitle class="no-wrap">{{ data.item.name }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </template>
          <template v-slot:selection="data">
            <v-chip class="caption | d-flex justify-center align-center"
                    :disabled="editMode" :close="!editMode"
                    @click:close="onRemoveSubordinateProduct">
              <div class="chip-ellipsis">{{ data.item.name }}</div>
            </v-chip>
          </template>
        </dynameat-autocomplete>
        <v-text-field v-model="ratio" :rules="numberRules" :label="$t('ratio')" class="required"/>
        <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm"
                                :confirmDisabled="!formValid"
                                :confirmText="$t('confirm')"/>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>

import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { Components } from '@core/helpers/decorators';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import DynameatAutocomplete from '@dynameat/components/DynameatAutocomplete';
import { notEmptyObject, notValidNumber } from '@core/helpers/formValidators';
import cloneDeep from 'lodash/cloneDeep';
import MasterProductModal from '@masters/views/product/components/MasterProductModal.vue';
import { productTextFilter } from '@core/mixins';
import { ProductIndexing } from '../models';

@Component
@Components({
  DynameatConfirmationModal, DynameatModalButtons, MasterProductModal, DynameatAutocomplete
})
export default class ProductIndexingModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible;

  @Prop({ type: Array, default: () => [] }) mainProductList;

  @Prop({ type: Set, default: () => new Set() }) associatedProducts;

  @Prop({ type: Boolean, default: false }) editMode;

  @Prop({ type: ProductIndexing }) productIndexing;

  @Prop({ type: String, default: '-1' }) productIndexingGuid;

  internalProductIndexing = null;

  valid = false;

  selectedMainProduct = null;

  selectedSubProduct = null;

  confirmModalVisible = false;

  ratio = null;

  masterProductModalVisible = false;

  productToEdit = null;

  get objectRules() {
    return [notEmptyObject(this.$t('emptyFieldError'))];
  }

  get defaultProductImg() {
    // eslint-disable-next-line global-require
    return require('@dynameat/assets/images/dynameat-logo-secondary.png');
  }

  get numberRules() {
    return [notValidNumber(this.$t('invalidNumberError'))];
  }

  get formValid() {
    return this.valid && this.selectedMainProduct !== this.selectedSubProduct;
  }

  get referenceProductList() {
    if (!this.editMode) {
      return this.mainProductList.filter(
        (product) => !this.associatedProducts.has(product.guid) && product.is_managed
      );
    }
    return this.mainProductList;
  }

  get subordinateProductList() {
    if (!this.editMode) {
      return this.mainProductList.filter((product) => !product.is_managed
        && !this.associatedProducts.has(product.guid) && this.selectedMainProduct !== product.guid);
    }
    return this.mainProductList;
  }

  get title() {
    return this.editMode ? this.$t('editTitle') : this.$t('createTitle');
  }

  async created() {
    this.clearOwnData();
  }

  onConfirm() {
    if (this.formValid) {
      this.onOpenConfirmModal();
    }
  }

  onCancel() {
    this.clearOwnData();
    this.$emit('cancel');
  }

  onRemoveMainProduct() {
    this.selectedMainProduct = null;
  }

  onRemoveSubordinateProduct() {
    this.selectedSubProduct = null;
  }

  onProductTextFilter = productTextFilter

  clearOwnData() {
    this.internalProductIndexing = null;
    this.valid = false;
    this.selectedMainProduct = null;
    this.selectedSubProduct = null;
    this.confirmModalVisible = false;
    this.ratio = null;
    this.masterProductModalVisible = false;
    this.productToEdit = null;
  }

  onOpenConfirmModal() {
    this.confirmModalVisible = true;
  }

  onCloseConfirmModal() {
    this.confirmModalVisible = false;
  }

  buildProductIndexing() {
    if (this.editMode) {
      this.internalProductIndexing.editRatio(this.selectedSubProduct, this.ratio);
    } else {
      this.internalProductIndexing = new ProductIndexing(this.selectedMainProduct);
      this.internalProductIndexing.addAssociatedProduct(this.productIndexingGuid, this.selectedSubProduct, parseFloat(this.ratio));
    }
    return this.internalProductIndexing;
  }

  async onConfirmConfirmModal() {
    this.confirmModalVisible = false;
    const productIndexing = this.buildProductIndexing();
    this.clearOwnData();
    const mode = this.editMode ? 'edit' : 'create';
    this.$emit(`${mode}Confirm`, { productIndexing, productIndexingGuid: this.productIndexingGuid });
  }

  onChangeSubProduct() {
    const subordinateProduct = this.subordinateProductList.find((product) => product.guid === this.selectedSubProduct);
    const needToSetupProduct = subordinateProduct?.is_new || !subordinateProduct?.isConfigured;
    if (!!subordinateProduct && needToSetupProduct && !this.isSubordinateProduct(subordinateProduct)) {
      this.productToEdit = subordinateProduct;
      this.masterProductModalVisible = true;
    }
  }

  isSubordinateProduct(product) {
    return this.mainProductList.some((el) => Object.values(el.sale_formats).includes(product.guid));
  }

  onCloseMasterProductModal(product) {
    if (!product) this.selectedSubProduct = null;
    this.productToEdit = null;
    this.masterProductModalVisible = false;
  }

  @Watch('visible')
  onVisible() {
    this.clearOwnData();
    if (this.visible && this.editMode) {
      this.internalProductIndexing = cloneDeep(this.productIndexing);
      this.selectedMainProduct = this.internalProductIndexing.referenceProductGuid;
      const associatedProduct = this.internalProductIndexing.associatedProducts.find((associate) => associate.productIndexingGuid === this.productIndexingGuid);
      this.selectedSubProduct = associatedProduct?.associatedProductGuid;
      this.ratio = `${associatedProduct?.ratio}`;
    }
  }
}
</script>
<style scoped>
.word-break {
  word-break: break-word;
}

.chip-ellipsis {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.h-scroll {
  overflow-y: hidden;
  overflow-x: auto;
}

.no-wrap {
  text-overflow: initial;
  overflow: initial;
  white-space: nowrap;
}

.required label::after {
  content: " *";
  color: red;
}
</style>
